import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Downloading, ModeStandbyOutlined, Receipt } from "@mui/icons-material";
import { toast } from "react-toastify";
import moment from "moment";

import { CardLayout, DeleteModal, LedgerModeToggle, MTable } from "../v3";
import { getLedgerTableColumns, getStatementTableColumns } from "../../pages/client/constants";
import { get_date } from "../generalComponets";
import {
  EXPORT_CLIENT_STATEMENT,
  DELETE_CLIENT_PAYMENT,
  EDIT_CLIENT_PAYMENT,
  EDIT_CLIENT_INCOME,
  DELETE_CLIENT_INCOME,
  VIEW_CLIENT_LEDGER_MODE_STATEMENT,
} from "../../constants";
import { AccountService, AnalyticsService, CRMService, FinanceService, ProjectService } from "../../services";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { getAccountName } from "../../utils/formatters";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";
import { renameCashAccount } from "../../utils/accountUtils";

export const ClientStatement = ({ name = "", onSuccess = () => {}, reloadData = 0 }) => {
  const [statements, setStatements] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    filter: selectedFilter,
    setFilter: setSelectedFilter,
    searchText,
    setSearchText,
    isLedgerMode: isSavedLedgerMode,
    setLedgerMode: setIsLedgerMode,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "statement",
    defaults: { filter: "ALL", sortModel: [{ field: "date", sort: "desc" }] },
    filterValues: ["ALL", "clientReceivable", "income"],
  });
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    statementId: null,
    type: "",
  });

  const navigate = useNavigate();
  const { id } = useParams();

  const { user } = useSelector((state) => state.user);

  const hasEditPayment = useHasPermission(EDIT_CLIENT_PAYMENT);
  const hasDeletePayment = useHasPermission(DELETE_CLIENT_PAYMENT);
  const hasEditIncome = useHasPermission(EDIT_CLIENT_INCOME);
  const hasDeleteIncome = useHasPermission(DELETE_CLIENT_INCOME);
  const hasExportStatement = useHasPermission(EXPORT_CLIENT_STATEMENT);
  const hasLedgerAccess = useHasPermission(VIEW_CLIENT_LEDGER_MODE_STATEMENT);
  // A remembered ledger toggle never outlives the permission.
  const isLedgerMode = hasLedgerAccess && isSavedLedgerMode;

  const fetchData = useCallback(
    async (statementFilters = {}) => {
      setLoading(true);

      try {
        const clientAnalytics = isLedgerMode
          ? await AnalyticsService.getClientLedger({ ...statementFilters, clientId: Number(id) })
          : await AnalyticsService.getClientAnalytics({ ...statementFilters, clientId: Number(id) });

        // gets the AccountIds and ProjectIds from the analytics response to get the account and project names
        const projectIds = Array.from(new Set(clientAnalytics?.data?.rows?.map((item) => item.projectId)));
        const toAccountIds = Array.from(new Set(clientAnalytics?.data?.rows?.map((item) => item.toAccountId)));

        setStatements(clientAnalytics?.data?.rows);
        setTotalCount(clientAnalytics?.data?.count);

        if (projectIds.length > 0) {
          const projectResponse = await ProjectService.getProjectList({
            ids: projectIds.join(","),
            limit: projectIds.length,
            includeDeleted: true,
          });
          setProjects(projectResponse?.data?.rows);
        }
        if (toAccountIds.length > 0) {
          const accountResponse = await AccountService.getAccounts({
            ids: toAccountIds.join(","),
            limit: toAccountIds.length,
            includeDeleted: true,
            includeOwnerDetails: true,
          });
          setAccounts(accountResponse?.data?.rows);
        }
      } catch (error) {
        console.log("🚀 ~ fetchData ~ error:", error);
      }

      setLoading(false);
    },
    [id, isLedgerMode],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      ...(!isLedgerMode && {
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        ...(selectedFilter !== "ALL" && { type: selectedFilter }),
        description: searchText,
      }),
    });
  }, [fetchData, sortModel, paginationState, selectedFilter, searchText, dateRange, reloadData, isLedgerMode]);

  const handleLedgerModeToggle = () => {
    setIsLedgerMode((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue);
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Convert selected dates to full-day ISO boundaries before sending API filters.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfTitle = pdfTitleMap[selectedFilter] || `Payments & Income : ${name}`;
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title={pdfTitle} columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: pdfTitle,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const onViewClick = (params) => {
    params?.row?.type === "income"
      ? navigate(`/client/receipt/${params.row.statementId}`)
      : navigate(`/client/invoice/${params.row.statementId}`);
  };

  const openDeleteModal = (statementData) => {
    setDeleteModal({
      isOpen: true,
      statementId: statementData.statementId,
      type: statementData.type,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      statementId: null,
      type: "",
    });
  };

  const handleDelete = async () => {
    try {
      if (deleteModal?.type === "income") {
        await FinanceService.deleteIncome(deleteModal?.statementId);
      } else if (deleteModal?.type === "clientReceivable") {
        await CRMService.deleteClientReceivables(id, deleteModal?.statementId);
      }

      toast.success(`${deleteModal.type === "income" ? "income" : "payment"} deleted successfully`);
      onSuccess?.();
    } catch (error) {
      toast("failed to delete income", { type: "error" });
      console.log("🚀 ~ deleteStatement ~ error:", error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = async (statementData, actionType) => {
    switch (actionType) {
      case "edit":
        if (statementData?.type === "income") {
          navigate(`/edit-income/${statementData?.statementId}`);
        } else {
          navigate(`/receivables/edit/${statementData?.statementId}`);
        }
        break;
      case "delete":
        openDeleteModal(statementData);
        break;
      default:
        break;
    }
  };

  const filterOptions = [
    { label: "ALL", value: "ALL", icon: <ModeStandbyOutlined /> },
    {
      label: "Payment",
      value: "clientReceivable",
      icon: <Downloading />,
    },
    { label: "Income", value: "income", icon: <Receipt /> },
  ];

  const pdfTitleMap = {
    ALL: `Payments & Income : ${name}`,
    clientReceivable: `Payments : ${name}`,
    income: `Income : ${name}`,
  };

  const columnHeaders = isLedgerMode
    ? getLedgerTableColumns(handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome)
    : getStatementTableColumns(handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome);

  const ledgerModeToggle = hasLedgerAccess ? (
    <LedgerModeToggle isLedgerMode={isLedgerMode} onToggle={handleLedgerModeToggle} />
  ) : null;

  const rows = statements.map((statement, index) => {
    const project = projects.find((a) => a.id === statement.projectId);
    const account = accounts.find((a) => a.id === statement.toAccountId);
    const isCashOrUser = ["CASH", "USER"].includes(account?.type);

    const accountDisplayName =
      (isCashOrUser && renameCashAccount(account, user, user.company.ownerUserId)?.name) ||
      getAccountName(account?.type, account);
    return {
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      id: index,
      statementId: statement?.id,
      date: get_date(statement.date),
      description: statement?.description,
      type: statement?.type,
      projectOrAccount: statement?.type === "income" ? accountDisplayName : project?.name,
      amount: Number(statement?.amount),
      balance: statement?.balance,
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Filter data
          selectedFilter={selectedFilter}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          showFilterButton
          disableFilterButton={isLedgerMode}
          // Search data
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          disableSearchBar={isLedgerMode}
          startAction={ledgerModeToggle}
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportStatement}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount || 0}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          disableSorting={isLedgerMode}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>

      {/* statement delete modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message={`want to delete this ${deleteModal.type === "income" ? "income" : "payment"}?`}
      />
    </>
  );
};
